import React from 'react';
import { Box, IconButton, Typography, Stack, useTheme, CircularProgress } from '@mui/material';
import {
  Close as CloseIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
} from '@mui/icons-material';
import type { CommandManager } from '../../commands/CommandManager';
import { IntercomLauncherToggle } from './IntercomLauncherToggle';

interface TopControlPanelProps {
  strategyName: string;
  blockName?: string;
  currentMode: 'blocks' | 'screens';
  commandManager: CommandManager;
  undoCount: number;
  redoCount: number;
  onClose: () => void;
  onBackToBlocks?: () => void;
  onThemeToggle?: () => void;
  onCommandExecuted?: () => void;
  isSyncing?: boolean;
  /** Selection-scoped actions rendered immediately to the left of custom actions. */
  leftCustomActions?: React.ReactNode;
  /** Custom actions to render between title and undo/redo buttons */
  customActions?: React.ReactNode;
  /** Custom actions to render after undo/redo controls */
  trailingActions?: React.ReactNode;
  /** Read-only surfaces (canvas report mode) suppress the undo/redo pair entirely. */
  hideHistoryControls?: boolean;
}

export const TopControlPanel: React.FC<TopControlPanelProps> = ({
  strategyName,
  blockName,
  currentMode,
  commandManager,
  undoCount,
  redoCount,
  onClose,
  onBackToBlocks,
  onThemeToggle,
  onCommandExecuted,
  isSyncing = false,
  leftCustomActions,
  customActions,
  trailingActions,
  hideHistoryControls = false,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const canUndo = commandManager.canUndo();
  const canRedo = commandManager.canRedo();

  const handleCloseClick = () => {
    if (currentMode === 'screens' && onBackToBlocks) {
      onBackToBlocks();
    } else {
      onClose();
    }
  };

  const handleUndo = async () => {
    if (canUndo) {
      await commandManager.undo();
      onCommandExecuted?.();
    }
  };

  const handleRedo = async () => {
    if (canRedo) {
      await commandManager.redo();
      onCommandExecuted?.();
    }
  };

  return (
    <Box
      sx={{
        height: 48,
        borderBottom: 1,
        borderColor: 'divider',
        px: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'background.paper',
      }}
    >
      {/* Left side: Close button + Strategy name */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: '8px' }}>
        <IconButton
          onClick={handleCloseClick}
          disabled={isSyncing}
          aria-label="close"
          data-testid="close-button"
          sx={{
            width: 29,
            height: 29,
            bgcolor: 'action.selected',
            borderRadius: '50%',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {isSyncing ? (
            <CircularProgress size={16} sx={{ color: 'inherit' }} />
          ) : (
            <CloseIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
        {currentMode === 'screens' && blockName ? (
          <Stack direction="column" spacing={0}>
            <Typography variant="h6" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
              {blockName}
            </Typography>
            {strategyName && (
              <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                {strategyName}
              </Typography>
            )}
          </Stack>
        ) : (
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {strategyName}
          </Typography>
        )}
      </Stack>

      {/* Spacer to push all controls to the right */}
      <Box sx={{ flex: 1 }} />

      {/* Right side: Custom actions + Undo/Redo + trailing actions + Theme toggle */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pr: 1 }}>
        {/* Selection-scoped actions. Because this stack is right-aligned, new
            left actions expand toward the title and keep Deploy/Actions stable. */}
        {leftCustomActions}

        {/* Custom actions (deploy web, version selector, actions menu) */}
        {customActions}

        {/* Undo/Redo */}
        {!hideHistoryControls && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton
              onClick={handleUndo}
              disabled={!canUndo}
              aria-label="undo"
              data-testid="undo-button"
              title={`Undo (Cmd/Ctrl+Z)\n${undoCount} operations in history`}
              size="small"
            >
              <UndoIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 20 }}>
              {undoCount}
            </Typography>
          </Stack>
        )}

        {!hideHistoryControls && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton
              onClick={handleRedo}
              disabled={!canRedo}
              aria-label="redo"
              data-testid="redo-button"
              title={`Redo (Cmd/Ctrl+Shift+Z)\n${redoCount} operations available`}
              size="small"
            >
              <RedoIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 20 }}>
              {redoCount}
            </Typography>
          </Stack>
        )}

        {trailingActions}

        {/* Theme toggle */}
        {onThemeToggle && (
          <IconButton
            onClick={onThemeToggle}
            aria-label="toggle theme"
            data-testid="theme-toggle-button"
            title="Toggle theme"
          >
            {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        )}

        {/* Show default Intercom launcher bubble while canvas is open */}
        <IntercomLauncherToggle />
      </Stack>
    </Box>
  );
};
